import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { useSettingsDialogStore } from 'state/store/settings-dialog/useSettingsDialogStore';
import { CREDITS_TAB_ID } from '../constants/credits';
import { useTopUpStatus } from '../hooks/use-credits';
import type { TopUp } from '../types';
import { topUpOutcome, type TopUpTone } from '../utils/topup-outcome';
import { TopUpToneIcon } from './topup-tone-icon';

interface ResultCardProps {
  tone: TopUpTone;
  title: string;
  description?: string;
  children?: ReactNode;
}

export const ResultCard = ({ tone, title, description, children }: ResultCardProps) => (
  <div className="flex min-h-screen w-full items-center justify-center bg-background p-4">
    <div
      role="status"
      aria-live="polite"
      className="w-full max-w-md space-y-6 rounded-lg border bg-card p-6 text-center text-card-foreground sm:p-8"
    >
      <TopUpToneIcon tone={tone} className="mx-auto h-16 w-16" />
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  </div>
);

/**
 * The page the browser returns to after the checkout page. It asks the server every 2
 * seconds for up to a minute and says what the server says, whatever the address bar claims.
 */
export const TopUpResult = ({ topUp: found }: { topUp: TopUp }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const openSettings = useSettingsDialogStore((state) => state.openSettings);
  const { topUp, isTimedOut } = useTopUpStatus(found.id, found);
  const { tone, title, description } = topUpOutcome(
    t,
    topUp,
    isTimedOut,
    t('CONFIRMING_PAYMENT', 'Confirming your payment...')
  );

  const tryAgain = () => {
    openSettings(CREDITS_TAB_ID);
    navigate('/chat');
  };

  return (
    <ResultCard tone={tone} title={title} description={description}>
      {tone === 'failure' && (
        <Button className="w-full sm:w-auto" onClick={tryAgain}>
          {t('TRY_AGAIN', 'Try Again')}
        </Button>
      )}
      {(tone === 'success' || tone === 'pending') && (
        <Button className="w-full sm:w-auto" onClick={() => navigate('/chat')}>
          {t('BACK_TO_CHAT', 'Back to chat')}
        </Button>
      )}
    </ResultCard>
  );
};
